import { isMapEditing } from './editlock';
// NASA FIRMS active-fire hotspots on the main map, served as vector tiles from
// /api/tiles/firms (migration 024). Always starts OFF.
//
// This used to fetch the whole window as one GeoJSON document from /api/firms;
// a 14-day theatre view is ~57k features and ~17 MB, which is survivable on
// localhost and hopeless over a real link. As tiles the browser fetches only
// what it is looking at, MVT dictionary-encodes the repeated satellite/source
// strings once per tile, and Martin caches them.
//
// Consequences of that move, both deliberate:
//   * The age window and "only unusual" are now setFilter calls against the
//     tile's `ts` (epoch seconds) and `unusual` properties — instant, no refetch.
//   * getCount() reports hotspots RENDERED IN THE VIEWPORT, not everything
//     loaded, because tiles outside the view were never fetched. panels.ts
//     labels it accordingly.
//
// studio.ts keeps its own GeoJSON overlay on /api/firms — a small fixed AOI
// where one request is cheaper than a tile pyramid.

import * as maplibregl from 'maplibre-gl';
import { sunPosition } from './daynight';
import { registerHover } from './hover';
import { localClock, zoneAt } from './localtime';

const SRC = 'firms';
const LAYER = 'firms-circles';    // the triangles — clicks, hover and the count
const GLOW = 'firms-glow';
const FOOTPRINT = 'firms-footprint';
const LAYERS = [GLOW, FOOTPRINT, LAYER];   // bottom to top
const SRC_LAYER = 'firms';        // == the layer name ST_AsMVT writes
// Below this the map shows the whole theatre and a single tile would carry the
// entire store; above it, overzoom reuses z10 tiles rather than cutting new
// ones. Both bounds are about how much a tile has to carry, not what's visible.
const MIN_ZOOM = 4;
const MAX_ZOOM = 10;
const POLL_MS = 10 * 60 * 1000;

const CONF_COLORS = { low: '#f0a43c', nominal: '#ef6c22', high: '#c92a2a' } as const;

// FIRMS hotspots draw as flame-triangles with a dark rim. Live events are
// age-coloured circles with a white rim, and both palettes live in the same
// red-orange band — at theatre zoom the two were indistinguishable dots.
// Shape is the cue that survives every zoom level and colour ramp.
//
// Drawn at 4x (72 px, pixelRatio 4 — still 18 CSS px at icon-size 1) because
// the layer now grows with zoom, and a 2x sprite blurred once enlarged. The
// thin LIGHT edge outside the dark rim is what keeps it visible on satellite
// imagery, where a dark rim on dark ground left only a small orange speck.
const SPRITE_PX = 72;

function drawTriangle(color: string): ImageData {
  const c = document.createElement('canvas');
  c.width = SPRITE_PX;
  c.height = SPRITE_PX;
  const ctx = c.getContext('2d')!;
  ctx.beginPath();
  ctx.moveTo(36, 9);
  ctx.lineTo(64, 61);
  ctx.lineTo(8, 61);
  ctx.closePath();
  ctx.lineJoin = 'round';
  ctx.lineWidth = 11;
  ctx.strokeStyle = 'rgba(255,255,255,.8)';
  ctx.stroke();
  ctx.fillStyle = color;
  ctx.fill();
  ctx.lineWidth = 5;
  ctx.strokeStyle = 'rgba(15,28,39,.92)';
  ctx.stroke();
  return ctx.getImageData(0, 0, SPRITE_PX, SPRITE_PX);
}

/** Register the per-confidence triangle images (idempotent). Shared with the
 *  Studio overlay so a hotspot looks the same everywhere. */
export function installFirmsImages(map: maplibregl.Map): void {
  const images: [string, string][] = [
    ['firms-tri-l', CONF_COLORS.low],
    ['firms-tri-n', CONF_COLORS.nominal],
    ['firms-tri-h', CONF_COLORS.high],
  ];
  for (const [name, color] of images) {
    if (!map.hasImage(name)) map.addImage(name, drawTriangle(color), { pixelRatio: 4 });
  }
}

const CONF_COLOR = ['match', ['get', 'conf'],
                    'l', CONF_COLORS.low, 'h', CONF_COLORS.high,
                    CONF_COLORS.nominal];

/** The triangle's scale by fire power, times `k` for the zoom. */
function frpSize(k: number): unknown[] {
  const at = (v: number): number => Number((v * k).toFixed(3));
  return ['interpolate', ['linear'], ['coalesce', ['get', 'frp'], 0],
          0, at(0.6), 5, at(0.7), 20, at(0.85), 100, at(1), 400, at(1.2)];
}

// Ground metres per screen pixel at zoom 0 (512-px tiles) at 30°N, the middle
// of the theatre — near enough for a footprint drawn as a circle anyway.
const M_PER_PX_Z0 = 78271.517 * Math.cos(Math.PI / 6);

/** Radius in pixels, at zoom `z`, of half a sensor pixel: VIIRS's I-band is
 *  375 m at nadir, MODIS 1 km. */
function footprintPx(z: number): unknown[] {
  const px = (m: number): number => Number((m / (M_PER_PX_Z0 / 2 ** z)).toFixed(2));
  return ['case', ['in', 'MODIS', ['coalesce', ['get', 'src'], '']],
          px(500), px(187.5)];
}

/** The three FIRMS layers, bottom to top — pure, so the style-spec test can
 *  reach them (a malformed expression throws inside addLayer).
 *
 *  HOTSPOTS WERE EASY TO MISS, worst zoomed in: a 7–18 px triangle of fixed
 *  size, a dark rim on dark imagery, and nothing marking how much ground the
 *  detection covers. Now the triangle grows with zoom (`zoom` the top-level
 *  input, fire power inside each stop — the only nesting MapLibre accepts), a
 *  soft glow in its confidence colour sits under every hotspot, and from z11
 *  the sensor pixel itself is drawn on the ground: the ~375 m VIIRS square,
 *  as a tinted circle, which is the honest size of "a hotspot here". */
export function firmsLayers(filter: unknown): maplibregl.LayerSpecification[] {
  const common = { source: SRC, 'source-layer': SRC_LAYER, filter: filter as never };
  return [
    {
      id: GLOW, type: 'circle', ...common, minzoom: MIN_ZOOM,
      paint: {
        'circle-color': CONF_COLOR as never,
        'circle-radius': ['interpolate', ['linear'], ['zoom'],
                          4, 7, 8, 10, 12, 15, 16, 22] as never,
        'circle-blur': 0.85,
        // Persistent sources (flares/foundries) stay faint; unusual glows.
        'circle-opacity': ['case', ['get', 'unusual'], 0.6, 0.12] as never,
      },
    },
    {
      id: FOOTPRINT, type: 'circle', ...common, minzoom: 11,
      paint: {
        'circle-color': CONF_COLOR as never,
        'circle-radius': ['interpolate', ['exponential', 2], ['zoom'],
                          11, footprintPx(11), 22, footprintPx(22)] as never,
        'circle-opacity': ['case', ['get', 'unusual'], 0.2, 0.06] as never,
        'circle-stroke-color': CONF_COLOR as never,
        'circle-stroke-width': 1.5,
        'circle-stroke-opacity': ['case', ['get', 'unusual'], 0.9, 0.3] as never,
        // Ground, not screen: it lies flat when the map is tilted.
        'circle-pitch-alignment': 'map',
        'circle-pitch-scale': 'map',
      },
    },
    {
      id: LAYER, type: 'symbol', ...common, minzoom: MIN_ZOOM,
      layout: {
        // VIIRS reports confidence as l/n/h; the numeric form only shows up in
        // MODIS feeds, which this store does not carry.
        'icon-image': ['match', ['get', 'conf'],
                       'l', 'firms-tri-l', 'h', 'firms-tri-h',
                       'firms-tri-n'] as never,
        // 11–22 px by fire power at z8, ~0.85x that at theatre zoom and up to
        // ~1.9x zoomed right in, where it used to stay at 7–18 px.
        'icon-size': ['interpolate', ['linear'], ['zoom'],
                      4, frpSize(0.85), 8, frpSize(1), 11, frpSize(1.3),
                      14, frpSize(1.6), 17, frpSize(1.9)] as never,
        // Never collision-cull: the viewport count reads rendered features,
        // and the server already decimated one detection per cell.
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
      paint: {
        // Persistent sources (flares/foundries) dim back; unusual pops.
        'icon-opacity': ['case', ['get', 'unusual'], 1, 0.4] as never,
      },
    },
  ] as maplibregl.LayerSpecification[];
}

let mapRef: maplibregl.Map | null = null;
let enabled = false;
let ageHours = 24;                // display window LENGTH, in hours
// Where the window ENDS, epoch ms. null = "now", a live trailing window that
// slides forward on each poll; a number pins it to a stretch of the past.
let untilMs: number | null = null;
let unusualOnly = false;          // hide the persistent flares/foundries
let count = 0;
let bust = 0;                     // bumped to pull fresh tiles past the cache
let pollTimer: number | undefined;
let layersReady = false;
let countHandlersReady = false;
const countCbs: ((n: number) => void)[] = [];

// ---------------------------------------------------------------------------

// 1440 h = 60 days = the rolling store's depth and the server's own cap.
function clampHours(hours: number): number {
  return Number.isFinite(hours) ? Math.min(1440, Math.max(1, hours)) : 24;
}

function windowEndMs(): number { return untilMs ?? Date.now(); }
function windowStartMs(): number { return windowEndMs() - ageHours * 3600_000; }

// The tile carries exactly the displayed window — not a superset the client
// then filters — which is what lets the server decimate overlapping detections
// at low zoom without ever hiding one that should show (see migration 024).
// That is also why a window ending in the past sends `until` rather than being
// trimmed here: the server keeps the newest detection per cell, so narrowing
// after the fact would leave each cell represented by a detection outside the
// range and drop the ones inside it (migration 027).
function tileUrl(): string {
  const until = untilMs == null ? '' : `&until=${Math.floor(untilMs / 1000)}`;
  return `${location.origin}/api/tiles/firms/{z}/{x}/{y}`
    + `?hours=${Math.round(ageHours)}${until}&v=${bust}`;
}

const esc = (v: unknown): string => String(v ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

// Which sensor saw it, from the tile's source/satellite. The GOES_NRT feed is
// FIRMS's whole geostationary ring — over this theatre the birds are Meteosat.
function instrumentLabel(src: unknown, sat: unknown): string {
  const s = String(src ?? '');
  if (s.startsWith('VIIRS')) return 'VIIRS';
  if (s.startsWith('MODIS')) return 'MODIS';
  if (s.startsWith('GOES')) {
    return String(sat ?? '').startsWith('Met') ? 'Meteosat' : 'GOES';
  }
  return 'Thermal';
}

function confidenceLabel(value: unknown): string {
  const raw = String(value ?? '').trim().toLowerCase();
  if (raw === 'h' || raw === 'high') return 'High';
  if (raw === 'l' || raw === 'low') return 'Low';
  if (raw === 'n' || raw === 'nominal' || raw === '') return 'Nominal';
  return /^\d+$/.test(raw)
    ? `${Number(raw) >= 80 ? 'High' : Number(raw) < 30 ? 'Low' : 'Nominal'} (${raw}%)`
    : 'Nominal';
}

function fmtDateTime(ms: number): string {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    timeZoneName: 'short',
  }).format(new Date(ms));
}

function ago(ms: number): string {
  const mins = Math.round((Date.now() - ms) / 60000);
  if (!Number.isFinite(mins)) return '';
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

function valueOrDash(value: unknown, suffix = ''): string {
  return value == null || value === '' ? '—' : `${value}${suffix}`;
}

// FIRMS's own day/night flag where the tile carries it. Without one, the same
// rule FIRMS applies: day when the solar zenith is under 85° (sun above 5°).
function isNightPass(dn: unknown, ms: number, lon: number, lat: number): boolean | null {
  if (dn === 'D') return false;
  if (dn === 'N') return true;
  if (!Number.isFinite(ms)) return null;
  return sunPosition(new Date(ms), lat, lon).altitude < 5;
}

function popupHtml(p: Record<string, unknown>, lon: number, lat: number): string {
  const ms = Number(p.ts) * 1000;
  const night = isNightPass(p.dn, ms, lon, lat);
  const pass = night === null ? ''
    : `<span class="fp-pass ${night ? 'fp-night' : 'fp-day'}">${night ? '☾ Night pass' : '☀ Day pass'}</span>`;
  const days = typeof p.bdays === 'number' ? p.bdays : null;
  const hits = typeof p.bhits === 'number' ? p.bhits : days;
  const n = hits ?? 0;
  const baseline = days === null ? ''
    : days === 0 ? '<div class="fp-baseline fp-new">New — not seen here in the baseline</div>'
    : p.unusual === true ? `<div class="fp-baseline fp-new">Unusual — only ${days} recent day${days === 1 ? '' : 's'} here</div>`
    // "last 60 days" = the baseline's WINDOW_DAYS — wider than the 14-day max
    // display window, so the recurrence evidence may not be on the map.
    : `<div class="fp-baseline fp-known">Persistent source — ${n} detection${n === 1 ? '' : 's'} over ${days} day${days === 1 ? '' : 's'} nearby in the last 60 days</div>`;
  return `<div class="firms-popup">
    <h4>${esc(instrumentLabel(p.src, p.sat))} thermal detection</h4>
    <div class="fp-time">${esc(Number.isFinite(ms) ? fmtDateTime(ms) : '—')} <span class="fp-whose">your time</span></div>
    ${Number.isFinite(ms) ? `<div class="fp-time"><span class="fp-local">…</span> <span class="fp-whose">there</span></div>` : ''}
    <div class="fp-ago">${esc(Number.isFinite(ms) ? ago(ms) : '')} ${pass}</div>
    ${baseline}
    <dl>
      <dt>Confidence</dt><dd>${esc(confidenceLabel(p.conf))}</dd>
      <dt>Fire power</dt><dd>${esc(valueOrDash(p.frp, ' MW'))}</dd>
      <dt>Satellite</dt><dd>${esc(valueOrDash(p.sat))}</dd>
      <dt>Instrument</dt><dd>${esc(instrumentLabel(p.src, p.sat))}</dd>
      <dt>Footprint</dt><dd>${String(p.src ?? '').startsWith('MODIS') ? '~1 km' : '~375 m'} sensor pixel</dd>
      <dt>Coordinates</dt><dd>${lat.toFixed(5)}, ${lon.toFixed(5)}</dd>
    </dl>
    <p class="fp-note">NASA FIRMS ${esc(p.src ?? '')}. A hotspot is a thermal anomaly,
    not by itself confirmation of a wildfire.</p>
  </div>`;
}

// ---------------------------------------------------------------------------

function whenStyleReady(map: maplibregl.Map, fn: () => void): void {
  if (map.isStyleLoaded() || (map.style as unknown as { _loaded?: boolean })?._loaded) fn();
  else map.once('style.load', fn);
}

/** "Only unusual", plus the window edges. The tile already contains just the
 *  requested window, so on a LIVE window the age clause only matters for the
 *  drift between tile refreshes — and because the server keeps the NEWEST
 *  detection in each decimated cell, ageing out a representative correctly
 *  ages out everything it stood for. On a pinned window it simply restates the
 *  tile's own bounds. */
function currentFilter(): maplibregl.FilterSpecification {
  const live = untilMs == null;
  const clauses: unknown[] = [
    'all',
    ['>=', ['get', 'ts'], Math.round(windowStartMs() / 1000)],
    // Mild clock skew only needs tolerating against "now"; a pinned end is an
    // edge the user chose and must hold exactly.
    ['<=', ['get', 'ts'], Math.round(windowEndMs() / 1000) + (live ? 3600 : 0)],
  ];
  if (unusualOnly) clauses.push(['==', ['get', 'unusual'], true]);
  return clauses as unknown as maplibregl.FilterSpecification;
}

function applyFilter(): void {
  const map = mapRef;
  if (map && layersReady && map.getLayer(LAYER)) {
    const filter = currentFilter();
    for (const id of LAYERS) if (map.getLayer(id)) map.setFilter(id, filter);
    recount();
  }
}

function setVisible(map: maplibregl.Map, on: boolean): void {
  for (const id of LAYERS) {
    if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', on ? 'visible' : 'none');
  }
}

function ensureLayers(map: maplibregl.Map): void {
  if (layersReady && map.getSource(SRC)) return;
  if (!map.getSource(SRC)) {
    map.addSource(SRC, {
      type: 'vector', tiles: [tileUrl()], minzoom: MIN_ZOOM, maxzoom: MAX_ZOOM,
    });
  }
  if (!map.getLayer(LAYER)) {
    installFirmsImages(map);
    for (const layer of firmsLayers(currentFilter())) {
      if (!map.getLayer(layer.id)) map.addLayer(layer);
    }
    // NOTE maplibre v5: Popup.on() returns a Subscription, so never chain
    // .addTo() after .on() — build the popup, then add it. The footprint is
    // clickable too: zoomed in, it is the hotspot.
    map.on('click', [LAYER, FOOTPRINT], (e) => {
      if (isMapEditing()) return;
      const f = e.features?.[0];
      if (!f || f.geometry.type !== 'Point') return;
      const [lon, lat] = f.geometry.coordinates as [number, number];
      const popup = new maplibregl.Popup({ closeButton: true, maxWidth: '270px' });
      popup.setLngLat([lon, lat]);
      const props = f.properties as Record<string, unknown>;
      popup.setHTML(popupHtml(props, lon, lat));
      popup.addTo(map);
      // The clock there needs the country under the point; fill it in when
      // the lookup lands (cached per 0.1° cell, solar time on a miss).
      const ms = Number(props.ts) * 1000;
      if (Number.isFinite(ms)) {
        void zoneAt(lon, lat).then((zone) => {
          const slot = popup.getElement()?.querySelector('.fp-local');
          if (slot) slot.textContent = localClock(ms, lon, zone);
        });
      }
    });
    registerHover(map, { layers: [LAYER, FOOTPRINT], cursor: 'pointer' });
  }
  if (!countHandlersReady) {
    // The count changes when the view moves, when a tile of THIS source lands,
    // or when the filter/window changes (applyFilter recounts itself) — so
    // those are the triggers. `idle` used to be the trigger, and MapLibre
    // fires `idle` after EVERY frame in which nothing is left dirty, which
    // with an animated marker on screen is every frame: the recount — a
    // queryRenderedFeatures over every hotspot in view — ran at the display's
    // refresh rate. It stays only as a low-rate fallback.
    map.on('moveend', () => scheduleRecount());
    map.on('sourcedata', (e) => { if (e.sourceId === SRC) scheduleRecount(); });
    map.on('idle', () => {
      if (performance.now() - lastRecountAt > IDLE_RECOUNT_MS) scheduleRecount();
    });
    countHandlersReady = true;
  }
  layersReady = true;
}

let recountTimer: number | undefined;
let lastRecountAt = -Infinity;
const RECOUNT_DEBOUNCE_MS = 300;
const IDLE_RECOUNT_MS = 5000;

/** Trailing debounce: one recount after the burst of tile/move events settles. */
function scheduleRecount(): void {
  if (recountTimer !== undefined) window.clearTimeout(recountTimer);
  recountTimer = window.setTimeout(() => {
    recountTimer = undefined;
    recount();
  }, RECOUNT_DEBOUNCE_MS);
}

function setCount(n: number): void {
  if (n === count) return;
  count = n;
  for (const cb of countCbs) cb(n);
}

/** Hotspots currently drawn in the viewport. Tiles outside the view were never
 *  fetched, so this is necessarily a "what you can see" number. */
function recount(): void {
  const map = mapRef;
  lastRecountAt = performance.now();
  if (!map || !enabled || !layersReady || !map.getLayer(LAYER)) {
    setCount(0);
    return;
  }
  if (map.getZoom() < MIN_ZOOM) { setCount(0); return; }
  try {
    setCount(map.queryRenderedFeatures({ layers: [LAYER] }).length);
  } catch {
    // style mid-reload — the next idle recounts
  }
}

function refreshTiles(): void {
  const map = mapRef;
  if (!map || !layersReady) return;
  bust += 1;
  const src = map.getSource(SRC) as maplibregl.VectorTileSource | undefined;
  src?.setTiles([tileUrl()]);
}

// --- public API (panels.ts owns the checkbox UI) ----------------------------

/** Turn the overlay on or off, optionally setting the window at the same time
 *  (`hours` = its length, `until` = epoch ms of its end, null/omitted = now). */
export function setEnabled(map: maplibregl.Map, on: boolean,
                           hours?: number, until?: number | null): void {
  mapRef = map;
  const nextUntil = until === undefined ? untilMs : until;
  const windowChanged = (hours !== undefined && clampHours(hours) !== ageHours)
    || nextUntil !== untilMs;
  if (hours !== undefined) ageHours = clampHours(hours);
  untilMs = nextUntil;
  if (on === enabled) {
    if (on && windowChanged) { refreshTiles(); applyFilter(); }
    return;
  }
  enabled = on;
  if (on) {
    whenStyleReady(map, () => {
      if (!enabled) return;  // toggled off again before the style loaded
      ensureLayers(map);
      if (windowChanged) refreshTiles();
      setVisible(map, true);
      applyFilter();
    });
    if (pollTimer === undefined) {
      pollTimer = window.setInterval(() => {
        if (!enabled || document.hidden) return;
        refreshTiles();   // pick up detections ingested since (backfill included)
        applyFilter();    // and slide a live window forward; a pinned one holds
      }, POLL_MS);
    }
  } else {
    if (pollTimer !== undefined) { window.clearInterval(pollTimer); pollTimer = undefined; }
    if (layersReady) setVisible(map, false);
    setCount(0);
  }
}

/** Change the display window: `hours` is its length (1 h .. 60 days) and
 *  `until` the epoch ms it ends at, or null for a live window ending now. The
 *  window is baked into the tile, so a change refetches — Martin caches per
 *  window, and the filter update keeps the map honest until they land. */
export function setWindow(hours: number, until: number | null = null): void {
  const before = ageHours;
  const beforeUntil = untilMs;
  ageHours = clampHours(hours);
  untilMs = until;
  if (ageHours !== before || untilMs !== beforeUntil) refreshTiles();
  applyFilter();
}

export function setUnusualOnly(on: boolean): void {
  unusualOnly = on;
  applyFilter();
}

export function getCount(): number {
  return count;
}

/** Subscribe to hotspot-count changes. Fires immediately with the current
 *  count; returns an unsubscribe function. */
export function onCountChange(cb: (n: number) => void): () => void {
  countCbs.push(cb);
  cb(count);
  return () => {
    const i = countCbs.indexOf(cb);
    if (i >= 0) countCbs.splice(i, 1);
  };
}
